import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Collapse, Row } from 'antd'
import { filter, includes, isNil, keys, map, size } from 'lodash'
import PropTypes from 'prop-types'

import {
  computeJsonDescriptorHighlights,
  handleAiHighlightStrategy,
} from '../../helpers'
import {
  StyledDescriptorImg,
  StyledDietCollapse,
  StyledDietDescriptorSpacing,
} from '../styled'

import AiHighlightDecoratedComponent from './ai-highlight-decorated-component'
import RichTextHighlightingInput from './rich-text-highlighting-input'

const JsonDescriptorCollapse = ({
  highlights,
  value,
  prefix,
  collapsedByDefault,
}) => {
  const [activeKey, setActiveKey] = useState('0')

  useEffect(() => {
    setActiveKey(collapsedByDefault ? '1' : '0')
  }, [collapsedByDefault])

  const mappedDescriptorValuesToHighlightKeys = useMemo(
    () => computeJsonDescriptorHighlights(prefix, value),
    [value, prefix],
  )

  const highlightingKeys = useMemo(
    () => keys(mappedDescriptorValuesToHighlightKeys),
    [mappedDescriptorValuesToHighlightKeys],
  )

  const fragmentsWithHighlights = useMemo(
    () =>
      filter(
        highlightingKeys,
        highlightKey => !isNil(highlights[highlightKey]),
      ),
    [highlightingKeys, highlights],
  )

  const handleClick = useCallback(
    e => {
      setActiveKey(activeKey === '0' ? '1' : '0')
      e.stopPropagation()
    },
    [activeKey],
  )

  return (
    <>
      <Row>
        {map(highlightingKeys, highlightKey => (
          <Fragment key={highlightKey}>
            {includes(highlightKey, 'imageSrc') && (
              <StyledDescriptorImg
                src={mappedDescriptorValuesToHighlightKeys[highlightKey]}
                alt="food"
              />
            )}
          </Fragment>
        ))}
        {map(fragmentsWithHighlights, (highlightKey, index) => (
          <Fragment key={highlightKey}>
            <RichTextHighlightingInput
              value={mappedDescriptorValuesToHighlightKeys[highlightKey]}
              highlights={highlights[highlightKey] || []}
              highlightStrategy={handleAiHighlightStrategy}
              decoratorComponent={AiHighlightDecoratedComponent}
              isReadOnly
            />
            {index + 1 !== size(highlightingKeys) && (
              <StyledDietDescriptorSpacing> </StyledDietDescriptorSpacing>
            )}
          </Fragment>
        ))}
      </Row>
      <div aria-hidden="true" onClick={handleClick}>
        <StyledDietCollapse
          className="json-collapse"
          ghost
          activeKey={[activeKey]}
        >
          <Collapse.Panel key="1" header="{ ... }">
            <Row>
              {map(highlightingKeys, highlightKey => (
                <Fragment key={highlightKey}>
                  <strong>{highlightKey.substring(size(prefix) + 1)}</strong>
                  {includes(highlightKey, 'imageSrc') && (
                    <StyledDescriptorImg
                      src={mappedDescriptorValuesToHighlightKeys[highlightKey]}
                      alt="food"
                    />
                  )}
                  <RichTextHighlightingInput
                    value={mappedDescriptorValuesToHighlightKeys[highlightKey]}
                    highlights={highlights[highlightKey] || []}
                    highlightStrategy={handleAiHighlightStrategy}
                    decoratorComponent={AiHighlightDecoratedComponent}
                    isReadOnly
                  />
                </Fragment>
              ))}
            </Row>
          </Collapse.Panel>
        </StyledDietCollapse>
      </div>
    </>
  )
}

JsonDescriptorCollapse.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  collapsedByDefault: PropTypes.bool,
  highlights: PropTypes.object,
  prefix: PropTypes.string,
}

JsonDescriptorCollapse.defaultProps = {
  collapsedByDefault: false,
  highlights: {},
  prefix: 'dietDescriptors',
}

export default JsonDescriptorCollapse
